import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../services/api';
import { TierBadge, TIERS, isTierCode, type TierCode } from '../billing/TierBadge';
import {
  deriveBillingState,
  formatCents,
  monthStartOf,
  todayIso,
  toCsv,
  billingStateLabel,
  type BillingAgentLike,
} from '../billing/billingState';

// Shapes from the server (Server #145 and #146). Kept loose on purpose: this
// tab reads and displays; it never mutates billing state.
export interface ClientSummary { id: string; name: string }
export interface ClientTierRow { clientId: string; clientName: string; code: TierCode | null; name: string; moduleCount: number; billableToday: number; totalAgents: number }
export interface InvoiceLine { clientId: string; clientName: string | null; tierCode: string | null; tierName: string | null; monthlyRateCents: number | null; agentDays: number; agents: number; amountCents: number | null }
export interface InvoicePreview { from: string; to: string; daysInPeriod: number; formula: string; lines: InvoiceLine[]; totals: { agentDays: number; pricedAgentDays: number; unpricedAgentDays: number; amountCents: number } }
export interface Coverage { from: string; to: string; daysInPeriod: number; capturedDays: number; interpolatedOnlyDays: number; missingDates: string[]; complete: boolean }
export interface AgentReportRow { agentId: string; clientId: string; clientName: string | null; hostname: string; plans: string[]; daysInRoster: number; billableDays: number; interpolatedBillableDays: number; excludedArchivedDays: number; excludedStaleDays: number; excludedNeverSeenDays: number; firstBillableDate: string | null; lastBillableDate: string | null; lastSeenAt: string | null; isArchived: boolean }

export interface BillingFixtures {
  clients: ClientTierRow[];
  preview: InvoicePreview | null;
  coverage: Coverage | null;
  report: AgentReportRow[];
  agents: BillingAgentLike[];
}

interface BillingSettingsProps {
  /** Harness and tests inject data here; the tab fetches when absent. */
  fixtures?: BillingFixtures;
  /** Period override for tests; defaults to the current month to date. */
  period?: { from: string; to: string };
}

const CONTACT = 'mailto:hello@dataanchor.io?subject=Data%20Anchor%20plan%20change';

const card = 'bg-white/[0.02] border border-white/[0.06] rounded-xl p-6';
const label = 'text-xs text-white/40 uppercase tracking-wider';
const th = 'text-left py-2 pr-4 text-[11px] font-medium uppercase tracking-wider text-white/40';
const td = 'py-2.5 pr-4 text-sm text-white/80 border-t border-white/[0.04]';
const num = `${td} font-mono tabular-nums`;

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Billing tab. Reads the tier of every client, this month's invoice preview
 * priced from tier history, the month's roster coverage, and the per-endpoint
 * report a disputed line is settled against. Tier changes go through the
 * payment flow, so "Change plan" is a contact action until that ships.
 */
const BillingSettings: React.FC<BillingSettingsProps> = ({ fixtures, period }) => {
  const { user } = useAuth();
  const today = todayIso();
  const from = period?.from ?? monthStartOf(today);
  const to = period?.to ?? today;

  const [clients, setClients] = useState<ClientTierRow[]>(fixtures?.clients ?? []);
  const [preview, setPreview] = useState<InvoicePreview | null>(fixtures?.preview ?? null);
  const [coverage, setCoverage] = useState<Coverage | null>(fixtures?.coverage ?? null);
  const [report, setReport] = useState<AgentReportRow[]>(fixtures?.report ?? []);
  const [loading, setLoading] = useState(!fixtures);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (fixtures) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const tenantId = user?.tenantId;
        const [clientList, agents, prev, cov, rep] = await Promise.all([
          tenantId ? api.getTenantClients(tenantId) : Promise.resolve([] as ClientSummary[]),
          api.getAgents(),
          api.getInvoicePreview(from, to),
          api.getRosterCoverage(from, to),
          api.getBillingAgentReport(from, to),
        ]);
        const agentList: Array<BillingAgentLike & { client?: { id: string } }> = Array.isArray(agents) ? agents : [];
        const rows: ClientTierRow[] = await Promise.all(
          (clientList as ClientSummary[]).map(async (c) => {
            let code: TierCode | null = null; let name = 'No tier'; let moduleCount = 0;
            try {
              const t = await api.getClientTier(c.id);
              if (t?.tier && isTierCode(t.tier.code)) { code = t.tier.code; name = t.tier.name; moduleCount = t.moduleCount ?? t.modules?.length ?? 0; }
            } catch { /* tier unknown: shown as No tier */ }
            const mine = agentList.filter((a) => a.client?.id === c.id);
            const billableToday = mine.filter((a) => deriveBillingState(a).state === 'billable' || deriveBillingState(a).state === 'silent_warning').length;
            return { clientId: c.id, clientName: c.name, code, name, moduleCount, billableToday, totalAgents: mine.length };
          })
        );
        if (cancelled) return;
        setClients(rows);
        setPreview(prev ?? null);
        setCoverage(cov ?? null);
        setReport(Array.isArray(rep?.agents) ? rep.agents : Array.isArray(rep) ? rep : []);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load billing data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fixtures, user?.tenantId, from, to]);

  const reportCsv = useMemo(() => toCsv(
    ['host', 'client', 'plans', 'days_in_roster', 'billable_days', 'inferred_billable_days', 'excluded_archived', 'excluded_stale', 'excluded_never_seen', 'first_billable', 'last_billable', 'last_seen', 'archived'],
    report.map((r) => [r.hostname, r.clientName ?? '', r.plans.join('|'), r.daysInRoster, r.billableDays, r.interpolatedBillableDays, r.excludedArchivedDays, r.excludedStaleDays, r.excludedNeverSeenDays, r.firstBillableDate ?? '', r.lastBillableDate ?? '', r.lastSeenAt ?? '', r.isArchived ? 'yes' : 'no'])
  ), [report]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-settings">
      {error && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 text-xs text-amber-300">{error}</div>
      )}

      {/* Plans per client */}
      <div className={card}>
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="text-lg font-medium text-white">Plans</h3>
          <span className="text-xs text-white/40">Per endpoint per month: {(['T1', 'T2', 'T3'] as TierCode[]).map((c) => `${TIERS[c].name} ${formatCents(TIERS[c].monthlyRateCents)}`).join(' / ')}</span>
        </div>
        {clients.length === 0 ? (
          <p className="text-sm text-white/50">No clients yet.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {clients.map((c) => (
              <div key={c.clientId} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4" data-testid="client-plan">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white truncate">{c.clientName}</div>
                    <div className="mt-1 text-xs text-white/50">{c.moduleCount} of 21 audit modules</div>
                  </div>
                  <TierBadge code={c.code} name={c.name} size="md" />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs text-white/60">
                    <span className="font-mono tabular-nums text-white">{c.billableToday}</span> of {c.totalAgents} endpoints billable today
                  </div>
                  <a href={CONTACT} className="text-xs text-anchor-blue hover:text-white transition-colors">Change plan</a>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-4 text-xs text-white/40">Upgrades apply immediately and bill at the new rate from that day. Downgrades take effect at the end of the billing period.</p>
      </div>

      {/* Invoice preview */}
      <div className={card}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <h3 className="text-lg font-medium text-white">This month so far</h3>
          <span className="text-xs font-mono text-white/40">{from} to {to}{preview ? ` (${preview.daysInPeriod} days)` : ''}</span>
        </div>
        <p className="text-xs text-white/40 mb-5">Preview from the daily record. The invoice is issued at month end and prices each endpoint-day at the plan in force that day.</p>
        {!preview || preview.lines.length === 0 ? (
          <p className="text-sm text-white/50">No billable endpoint-days recorded in this period yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Client</th><th className={th}>Plan</th><th className={`${th} text-right`}>Endpoint-days</th><th className={`${th} text-right`}>Endpoints</th><th className={`${th} text-right`}>Rate / month</th><th className={`${th} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((l, i) => (
                  <tr key={`${l.clientId}-${l.tierCode}-${i}`}>
                    <td className={td}>{l.clientName ?? l.clientId}</td>
                    <td className={td}><TierBadge code={l.tierCode} name={l.tierName ?? undefined} /></td>
                    <td className={`${num} text-right`}>{l.agentDays}</td>
                    <td className={`${num} text-right`}>{l.agents}</td>
                    <td className={`${num} text-right`}>{formatCents(l.monthlyRateCents)}</td>
                    <td className={`${num} text-right`}>{formatCents(l.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className={`${td} font-medium text-white`} colSpan={2}>Total</td>
                  <td className={`${num} text-right text-white`}>{preview.totals.agentDays}</td>
                  <td className={td} />
                  <td className={td} />
                  <td className={`${num} text-right text-white font-medium`}>{formatCents(preview.totals.amountCents)}</td>
                </tr>
              </tfoot>
            </table>
            {preview.totals.unpricedAgentDays > 0 && (
              <p className="mt-2 text-xs text-amber-300">{preview.totals.unpricedAgentDays} endpoint-days have no plan on record for their date and are not priced.</p>
            )}
          </div>
        )}
        {coverage && (
          <p className="mt-4 text-xs text-white/50" data-testid="coverage-line">
            <span className={label}>Record</span>{' '}
            {coverage.capturedDays} of {coverage.daysInPeriod} days recorded, {coverage.interpolatedOnlyDays} inferred, {coverage.missingDates.length} missing
            {coverage.missingDates.length > 0 && <span className="text-amber-300"> ({coverage.missingDates.join(', ')})</span>}
          </p>
        )}
      </div>

      {/* Per-endpoint report */}
      <div className={card}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
          <div>
            <h3 className="text-lg font-medium text-white">Per-endpoint report</h3>
            <p className="text-xs text-white/40 mt-1">Every host that appeared in the record this month, with the days it was billed and the days it was not, and why.</p>
          </div>
          <button
            type="button"
            onClick={() => downloadCsv(`anchor-billing-${from}-to-${to}.csv`, reportCsv)}
            disabled={report.length === 0}
            className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/70 hover:text-white hover:border-white/30 disabled:opacity-40 transition-colors"
          >
            Download CSV
          </button>
        </div>
        {report.length === 0 ? (
          <p className="text-sm text-white/50">No roster rows in this period yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Host</th><th className={th}>Client</th><th className={th}>Plan</th><th className={`${th} text-right`}>Billed days</th><th className={`${th} text-right`}>Inferred</th><th className={`${th} text-right`}>Not billed</th><th className={th}>Last seen</th><th className={th}>State</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r) => {
                  const excluded = r.excludedArchivedDays + r.excludedStaleDays + r.excludedNeverSeenDays;
                  const reason = r.excludedArchivedDays ? 'archived' : r.excludedStaleDays ? 'stale' : r.excludedNeverSeenDays ? 'never seen' : '';
                  const state = deriveBillingState({ lastSeenAt: r.lastSeenAt, isArchived: r.isArchived });
                  return (
                    <tr key={r.agentId}>
                      <td className={`${td} font-mono`}>{r.hostname}</td>
                      <td className={td}>{r.clientName ?? ''}</td>
                      <td className={td}>{r.plans.map((p) => <TierBadge key={p} code={p} className="mr-1" />)}</td>
                      <td className={`${num} text-right`}>{r.billableDays}</td>
                      <td className={`${num} text-right`}>{r.interpolatedBillableDays}</td>
                      <td className={`${num} text-right`}>{excluded}{excluded > 0 && reason ? <span className="text-white/40"> ({reason})</span> : null}</td>
                      <td className={`${td} text-white/60`}>{r.lastSeenAt ? new Date(r.lastSeenAt).toLocaleDateString() : 'never'}</td>
                      <td className={td}>{billingStateLabel(state.state, state.silentDays)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingSettings;
